import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Car, Wrench as WrenchIcon, Fuel } from "lucide-react";

const fmtMoney = (n, ccy = "INR") =>
    n == null ? "—" : `${ccy === "INR" ? "₹" : ccy + " "}${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function VehicleDetail() {
    const { id } = useParams();
    const [v, setV] = useState(null);
    const [logs, setLogs] = useState([]);
    const [summary, setSummary] = useState({ total_spent: 0, by_kind: [] });
    const [meta, setMeta] = useState({ log_kinds: [] });
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [f, setF] = useState({
        date: new Date().toISOString().slice(0, 10),
        kind: "Fuel", amount: "", vendor: "", odometer: "", quantity: "", reference: "", notes: "",
    });

    const load = useCallback(async () => {
        try {
            const [vr, lr, sr] = await Promise.all([
                api.get(`/vehicles/${id}`),
                api.get(`/vehicles/${id}/logs`),
                api.get(`/vehicles/${id}/summary`),
            ]);
            setV(vr.data);
            setLogs(lr.data || []);
            setSummary(sr.data || {});
        } catch (err) { toast.error(formatApiError(err)); }
    }, [id]);
    useEffect(() => {
        api.get("/vehicles/meta/enums").then((r) => setMeta(r.data || { log_kinds: [] }));
    }, []);
    useEffect(() => { load(); }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const body = { ...f };
            ["amount", "odometer", "quantity"].forEach((k) => {
                if (body[k] === "" || body[k] == null) delete body[k]; else body[k] = Number(body[k]);
            });
            await api.post(`/vehicles/${id}/logs`, body);
            toast.success("Log added");
            setOpen(false);
            setF({ ...f, amount: "", vendor: "", odometer: "", quantity: "", reference: "", notes: "" });
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const removeLog = async (logId) => {
        if (!window.confirm("Delete this log entry?")) return;
        try { await api.delete(`/vehicles/${id}/logs/${logId}`); toast.success("Deleted"); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    if (!v) return <div className="text-stone-500 text-sm">Loading…</div>;

    return (
        <div data-testid="vehicle-detail-page">
            <Link to="/vehicles" className="inline-flex items-center gap-1.5 text-stone-500 hover:text-stone-900 text-[12px] mb-4">
                <ArrowLeft size={13} /> All vehicles
            </Link>
            <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow flex items-center gap-1.5"><Car size={12} /> {v.kind}</div>
                    <h1 className="font-display text-3xl sm:text-4xl text-stone-900 leading-none mt-2">{v.name}</h1>
                    <div className="text-sm text-stone-500 mt-2">
                        {[v.make, v.model, v.year].filter(Boolean).join(" · ") || "—"}
                        {v.reg_number && <> · <span className="font-mono-figure">{v.reg_number}</span></>}
                    </div>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="veh-log-btn">
                            <Plus size={14} className="mr-1.5" /> Log entry
                        </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="veh-log-dialog">
                        <DialogHeader>
                            <DialogTitle className="font-display text-2xl">New log entry</DialogTitle>
                            <DialogDescription className="text-[13px] text-stone-500">
                                Fuel, service, tyres, challan, EMI or accessory.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={submit} className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label className="eyebrow">Date</Label>
                                    <Input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="mt-1.5" /></div>
                                <div><Label className="eyebrow">Kind</Label>
                                    <Select value={f.kind} onValueChange={(v_) => setF({ ...f, kind: v_ })}>
                                        <SelectTrigger className="mt-1.5" data-testid="vl-kind"><SelectValue /></SelectTrigger>
                                        <SelectContent>{meta.log_kinds.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div><Label className="eyebrow">Amount</Label>
                                    <Input type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="mt-1.5 font-mono-figure" data-testid="vl-amount" /></div>
                                <div><Label className="eyebrow">Vendor</Label>
                                    <Input value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} className="mt-1.5" /></div>
                                <div><Label className="eyebrow">Odometer (km)</Label>
                                    <Input type="number" value={f.odometer} onChange={(e) => setF({ ...f, odometer: e.target.value })} className="mt-1.5 font-mono-figure" /></div>
                                <div><Label className="eyebrow">Quantity (litres / kWh)</Label>
                                    <Input type="number" step="0.001" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} className="mt-1.5 font-mono-figure" /></div>
                                <div className="col-span-2"><Label className="eyebrow">Reference / Bill no</Label>
                                    <Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} className="mt-1.5 font-mono-figure" /></div>
                                <div className="col-span-2"><Label className="eyebrow">Notes</Label>
                                    <Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1.5" /></div>
                            </div>
                            <Button type="submit" disabled={busy} className="w-full bg-stone-900 hover:bg-stone-800 text-white" data-testid="vl-submit">
                                {busy ? "Saving…" : "Save entry"}
                            </Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                <div className="rounded-lg border border-stone-200 bg-white p-5">
                    <div className="eyebrow">Total spent</div>
                    <div className="font-mono-figure text-3xl mt-1 text-stone-900">{fmtMoney(summary.total_spent)}</div>
                    <div className="text-[11px] text-stone-500 mt-1">{logs.length} log entries</div>
                </div>
                <div className="rounded-lg border border-stone-200 bg-white p-5 lg:col-span-2">
                    <div className="eyebrow mb-2">Spend by kind</div>
                    <div className="flex flex-wrap gap-2">
                        {(summary.by_kind || []).map((k) => (
                            <span key={k.kind} className="text-[12px] px-3 py-1 rounded-full border border-stone-200 bg-stone-50">
                                <span className="text-stone-500">{k.kind}</span>
                                <span className="font-mono-figure ml-2 text-stone-900">{fmtMoney(k.total)}</span>
                            </span>
                        ))}
                        {(summary.by_kind || []).length === 0 && <span className="text-stone-400 text-xs">No spend yet.</span>}
                    </div>
                </div>
            </div>

            <div className="rounded-lg border border-stone-200 bg-white overflow-hidden">
                <div className="px-4 py-3 border-b border-stone-100 flex items-center gap-2">
                    <WrenchIcon size={13} className="text-stone-500" />
                    <div className="text-sm font-medium text-stone-900">Activity log</div>
                </div>
                <div className="overflow-x-auto">
                    <table className="dense-table w-full">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Kind</th>
                                <th>Vendor / Reference</th>
                                <th className="text-right">Odometer</th>
                                <th className="text-right">Qty</th>
                                <th className="text-right">Amount</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map((l) => (
                                <tr key={l.id} data-testid={`vl-row-${l.id}`}>
                                    <td className="tabular-nums">{fmtDate(l.date)}</td>
                                    <td>
                                        <span className="inline-flex items-center gap-1 text-stone-700 text-[12px]">
                                            {l.kind === "Fuel" && <Fuel size={11} />}
                                            {l.kind}
                                        </span>
                                    </td>
                                    <td className="text-[12px] text-stone-600">
                                        {l.vendor || "—"}
                                        {l.reference && <span className="text-stone-400 ml-2">· {l.reference}</span>}
                                    </td>
                                    <td className="text-right font-mono-figure text-[12px]">{l.odometer ?? "—"}</td>
                                    <td className="text-right font-mono-figure text-[12px]">{l.quantity ?? "—"}</td>
                                    <td className="text-right font-mono-figure">{fmtMoney(l.amount)}</td>
                                    <td>
                                        <button className="text-stone-400 hover:text-red-600 p-1" onClick={() => removeLog(l.id)} aria-label="Delete">
                                            <Trash2 size={12} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {logs.length === 0 && (
                                <tr><td colSpan={7} className="text-center text-stone-500 py-10">No log entries yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
